/**
 * OPS-STAB-009 — documents permission expansion contract (implemented in admin-portal Java).
 * Unregistered modules default to CRUD suffix expansion for backward compatibility.
 */

export const MODULE_TYPE = Object.freeze({
  CRUD: 'CRUD',
  OPERATIONAL: 'OPERATIONAL',
  /** OPS-MASTER-002 — setup / master-data modules */
  MASTER_DATA: 'MASTER_DATA',
});

export const ACCESS_LEVEL = Object.freeze({
  READ: 'read',
  READ_WRITE: 'read/write',
  NO_ACCESS: 'noaccess',
});

/** Legacy CRUD suffix expansion — must match AccessLevelConstants.java */
export const CRUD_SUFFIXES = Object.freeze({
  [ACCESS_LEVEL.READ]: ['', ':LIST', ':READ', ':VIEW'],
  [ACCESS_LEVEL.READ_WRITE]: [
    '',
    ':CREATE',
    ':EDIT',
    ':LIST',
    ':NEW',
    ':READ',
    ':UPDATE',
    ':UPDATESTATUS',
    ':VIEW',
  ],
  [ACCESS_LEVEL.NO_ACCESS]: [],
});

/** Master-data suffix expansion — must match MasterAccessLevelConstants.java */
export const MASTER_SUFFIXES = Object.freeze({
  [ACCESS_LEVEL.READ]: ['', ':VIEW'],
  [ACCESS_LEVEL.READ_WRITE]: ['', ':VIEW', ':CONFIGURE', ':APPROVE', ':ADMIN_OVERRIDE'],
  [ACCESS_LEVEL.NO_ACCESS]: [],
});

export const MASTER_PERMISSION_LEVELS = ['VIEW', 'CONFIGURE', 'APPROVE', 'ADMIN_OVERRIDE'];

export const WORKFLOW_ACTION_SCOPE = Object.freeze({
  READ: 'R',
  WRITE: 'W',
  BOTH: 'B',
});

/**
 * @param {string} scope R | W | B
 * @param {boolean} readWrite
 */
export function shouldGrantWorkflowAction(scope, readWrite) {
  const s = String(scope ?? 'B').toUpperCase();
  if (s === WORKFLOW_ACTION_SCOPE.BOTH || s === WORKFLOW_ACTION_SCOPE.READ) return true;
  if (s === WORKFLOW_ACTION_SCOPE.WRITE) return readWrite;
  return readWrite;
}

/**
 * @param {object} moduleRegistry row with moduleType, workflowGroup, basePermission
 * @param {string} accessLevel
 * @param {Array<{actionPermission: string, actionAccessScope: string}>} workflowRows
 */
export function expandModulePermissions(moduleRegistry, accessLevel, workflowRows = []) {
  if (!moduleRegistry || accessLevel === ACCESS_LEVEL.NO_ACCESS) return [];

  const base = moduleRegistry.basePermission;
  if (moduleRegistry.moduleType === MODULE_TYPE.MASTER_DATA) {
    const suffixes = MASTER_SUFFIXES[accessLevel] ?? [];
    return suffixes.map((s) => `${base}${s}`);
  }
  if (moduleRegistry.moduleType !== MODULE_TYPE.OPERATIONAL) {
    const suffixes = CRUD_SUFFIXES[accessLevel] ?? [];
    return suffixes.map((s) => `${base}${s}`);
  }

  const readWrite = accessLevel === ACCESS_LEVEL.READ_WRITE;
  if (accessLevel !== ACCESS_LEVEL.READ && !readWrite) return [];

  const out = [base];
  for (const row of workflowRows) {
    if (shouldGrantWorkflowAction(row.actionAccessScope, readWrite)) {
      out.push(row.actionPermission);
    }
  }
  return out;
}
